/**
 * HOẠT ẢNH THỦ TỤC — sinh từ xương, không tốn ảnh.
 * idle · chạy · đánh · trúng đòn · chết · niệm chiêu.
 *
 * Mọi thao tác xương đều đi qua `b()` (tra xương không ném lỗi) để cùng một
 * hàm pose dùng được cho CẢ nhân vật lẫn quái (quái có ít xương hơn).
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ĐÒN VÕ CẬN CHIẾN — cách tổ chức (làm lại, xem README "Cận chiến đánh võ"):
 *
 * Mỗi thế võ là HAI tư thế khoá: LẤY ĐÀ (kéo người về sau, rootX ÂM) và
 * RA ĐÒN (lao tới, rootX DƯƠNG + squash/stretch). Tư thế thứ ba — THU VỀ —
 * không cần khai riêng: nó là đường trộn từ tư thế RA ĐÒN về lại tư thế ĐỨNG YÊN
 * (`BASE`), kèm một dao động tắt dần cho cảm giác đàn hồi.
 *
 * Vì sao trộn từ `BASE` mà không "đặt thẳng" góc như bản cũ:
 * bản cũ nhịp ra đòn xong là `swing` về 0 ⇒ pose NHẢY PHẮT về dáng đứng
 * (mắt thấy giật). Trộn theo tỉ lệ ⇒ nhịp thu về khép kín, không giật.
 *
 * ⚠️ BÀI HỌC ĐÃ SẬP (giữ nguyên, đừng "tối ưu"):
 * 1) KHÔNG nhân `facing` vào scaleX của xương — ma trận định thức âm làm
 *    nhân vật LỘN NGƯỢC. Lật ngang do lớp sprite làm (render/unitview.ts).
 * 2) Trong hệ xương này tay/chân trỏ xuống theo +y nên hướng chi là
 *    `(−sinθ, cosθ)`. ĐỪNG đoán dấu góc cho từng bên tay: khai báo HƯỚNG MONG MUỐN
 *    `(dx, dy)` rồi để `dir()` tự tính góc. "Hai tay đưa ra trước" thì CÙNG dấu;
 *    "dang hai tay sang hai bên" mới đối dấu.
 * 3) Biên độ phải LỚN mới đọc ra ở cỡ nhân vật ~100px (bài học `kickCombo`
 *    phải xoay hông tới ~1,9 rad). Nhưng cũng đừng vượt vùng đã chứng minh an toàn
 *    (~±2,0 rad cho hông, ~±2,2 rad cho vai) kẻo chân bung khỏi mảnh quần TĨNH.
 * 4) Quái thiếu xương (không handL/handR, có con không kneeL/R) ⇒ TUYỆT ĐỐI
 *    không truy cập `rig.bone()` trực tiếp, chỉ dùng `b()`.
 */
import type { Bone, Rig } from '../rig/rig';
import { applyCastAnim } from './cast-anims';
import type { UnitState } from '../sim/types';

export interface PoseInput {
  state: UnitState;
  t: number;
  swing: number;
  facing: 1 | -1;
  hpPct: number;
  seed: number;
  deadFor?: number;
  hitFlash: number;
  isCasting?: boolean;
  /** thế võ đang ra: punch | hook | uppercut | knee | … (xem data/combat-moves.ts) */
  move?: string;
  /** dáng RA CHIÊU đang diễn (data/skill-anims.ts) */
  cast?: string | null;
  /** tiến trình 0..1 của dáng ra chiêu */
  castT?: number;
}

const TAU = Math.PI * 2;
const noop: Bone = {
  name: '_',
  parent: null,
  x: 0,
  y: 0,
  rot: 0,
  sx: 1,
  sy: 1,
  local: () => ({ a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 }),
  world: { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 },
} as unknown as Bone;

/** Hệ trục: x dương = hướng nhân vật đang nhìn, y dương = xuống. */
const FWD = 1;
const BACK = -1;
const UP = -1;
const DOWN = 1;

/**
 * Góc xoay để chi (tay/chân) trỏ theo hướng (dx, dy).
 * Từ hướng = (−sinθ, cosθ) ⇒ θ = atan2(−dx, dy).
 */
function dir(dx: number, dy: number): number {
  return Math.atan2(-dx, dy);
}

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}
function easeOutCubic(x: number): number {
  const c = clamp01(x);
  return 1 - Math.pow(1 - c, 3);
}
function easeInCubic(x: number): number {
  const c = clamp01(x);
  return c * c * c;
}
function easeInOutCubic(x: number): number {
  const c = clamp01(x);
  return c < 0.5 ? 4 * c * c * c : 1 - Math.pow(-2 * c + 2, 3) / 2;
}

/**
 * Nhịp của MỘT cú đánh, tính theo `prog = 1 - swing`:
 *   [0 · windEnd]            → LẤY ĐÀ
 *   [windEnd · hitEnd]       → RA ĐÒN (easeOutCubic: bung nhanh rồi chậm dần)
 *   [hitEnd · 1]             → THU VỀ (đàn hồi)
 * Xuất ra để test đo được ĐÚNG khung hình của từng nhịp (xem tests/moves.test.ts).
 */
export const MELEE_PHASE = { windEnd: 0.26, hitEnd: 0.74 } as const;

/** Mọi khoá mà một thế võ có thể đặt. */
type Key =
  | 'shoulderL' | 'shoulderR' | 'elbowL' | 'elbowR' | 'handL' | 'handR'
  | 'hipL' | 'hipR' | 'kneeL' | 'kneeR' | 'footL' | 'footR'
  | 'torso' | 'head' | 'rootRot'
  | 'rootX' | 'rootY' | 'rootSx' | 'rootSy';

type Keys = Partial<Record<Key, number>>;

/** Xương XOAY được (lọc sẵn để vòng lặp không phải if). */
const ROT_KEYS: Key[] = [
  'torso', 'head',
  'shoulderL', 'shoulderR', 'elbowL', 'elbowR', 'handL', 'handR',
  'hipL', 'hipR', 'kneeL', 'kneeR', 'footL', 'footR',
];

/**
 * TƯ THẾ ĐỨNG YÊN (thở nhẹ) — mọi thế võ trộn từ đây ra.
 * Cũng chính là dáng lúc idle nên nhịp THU VỀ khép kín, không bị "giật" về.
 */
const BASE: Record<Key, number> = {
  torso: 0.02, head: 0,
  shoulderL: 0.2, shoulderR: -0.2, elbowL: 0.45, elbowR: 0.45, handL: 0, handR: 0,
  hipL: 0.04, hipR: -0.04, kneeL: 0.05, kneeR: 0.05, footL: 0, footR: 0,
  rootRot: 0, rootX: 0, rootY: 0, rootSx: 1, rootSy: 1,
};

interface MeleePose {
  /** LẤY ĐÀ: dồn trọng tâm, kéo người/hông về sau (rootX âm) */
  wind: Keys;
  /** RA ĐÒN: lao tới (rootX dương) + squash/stretch */
  hit: Keys;
  /** độ đàn hồi khi thu về (0..1, mặc định 0.6): càng lớn càng "nảy" */
  spring?: number;
}

/**
 * BÀI VÕ — hình dáng cụ thể của từng thế. `id` khớp đúng khoá trong
 * `data/combat-moves.ts` (test `tests/moves.test.ts` kiểm không thiếu thế nào).
 */
const MELEE_POSES: Record<string, MeleePose> = {
  // ───────────────────────────────────────────────────────── ĐẤM THẲNG
  punch: {
    wind: {
      // kéo nắm đấm về sau, hạ trọng tâm xuống chân sau
      shoulderR: dir(BACK * 0.55, DOWN * 0.84), elbowR: 1.55, handR: 0.15,
      shoulderL: dir(FWD * 0.55, DOWN * 0.84), elbowL: 1.1,
      torso: -0.2, head: 0.06,
      hipR: 0.3, hipL: -0.15, kneeR: 0.5, kneeL: 0.18,
      rootX: -2.5, rootY: 1, rootSx: 0.98, rootSy: 1.03,
    },
    hit: {
      // duỗi thẳng tay, xoay vai + hông tới, người dài ra (stretch ngang)
      shoulderR: dir(FWD * 0.98, UP * 0.2), elbowR: 0.06, handR: 0,
      shoulderL: dir(FWD * 0.4, DOWN * 0.92), elbowL: 1.15,
      torso: 0.32, head: -0.1,
      hipR: -0.42, hipL: 0.5, kneeR: 0.55, kneeL: 0.2,
      rootX: 3.5, rootY: -1.2, rootSx: 1.05, rootSy: 0.96,
    },
  },

  // ─────────────────────────────────────────────────────────── MÓC CÂU
  hook: {
    wind: {
      // tay vung ra sau - lên, cả người xoay ngược lấy đà
      shoulderR: dir(BACK * 0.95, UP * 0.3), elbowR: 1.35, handR: 0.2,
      shoulderL: dir(FWD * 0.6, DOWN * 0.8), elbowL: 1.0,
      torso: -0.42, head: 0.12,
      hipR: 0.4, hipL: -0.3, kneeR: 0.45, kneeL: 0.2,
      rootX: -3, rootY: 1.5, rootSx: 0.97, rootSy: 1.04,
    },
    hit: {
      // quét ngang hết tầm: tay đi ~3 rad, người xoay theo
      shoulderR: dir(FWD * 0.9, DOWN * 0.44), elbowR: 0.75, handR: 0,
      shoulderL: dir(BACK * 0.5, DOWN * 0.87), elbowL: 1.2,
      torso: 0.55, head: -0.22,
      hipR: -0.5, hipL: 0.6, kneeR: 0.6, kneeL: 0.25,
      rootX: 4, rootY: -0.8, rootSx: 1.06, rootSy: 0.95,
    },
  },

  // ─────────────────────────────────────────────────────── MÓC NGƯỢC
  uppercut: {
    wind: {
      // khuỵu hẳn xuống, tay hạ thấp sau hông
      shoulderR: dir(BACK * 0.45, DOWN * 0.89), elbowR: 2.0,
      shoulderL: dir(FWD * 0.5, DOWN * 0.87), elbowL: 1.2,
      torso: 0.4, head: -0.25,
      hipR: -0.45, hipL: 0.5, kneeR: 1.0, kneeL: 1.0, footR: -0.3,
      rootX: -2.5, rootY: 8, rootSx: 1.04, rootSy: 0.92,
    },
    hit: {
      // bật lên: tay móc vọt lên trước, người giãn dọc
      shoulderR: dir(FWD * 0.85, UP * 0.53), elbowR: 1.9,
      shoulderL: dir(BACK * 0.4, DOWN * 0.92), elbowL: 1.0,
      torso: -0.35, head: 0.2,
      hipR: -0.6, hipL: 0.3, kneeR: 0.3, kneeL: 0.25,
      rootX: 2.5, rootY: -5, rootSx: 0.95, rootSy: 1.1,
    },
  },

  // ───────────────────────────────────────────────────────── PHẢN CHỎ
  backfist: {
    wind: {
      // tay bắt chéo ngực (chĩa hẳn sang trái), người co lại
      shoulderR: dir(BACK, 0), elbowR: 1.8, handR: 0.3,
      shoulderL: dir(BACK * 0.4, DOWN * 0.92), elbowL: 1.1,
      torso: -0.3, head: 0.1,
      hipR: 0.3, hipL: -0.25, kneeR: 0.45, kneeL: 0.2,
      rootX: -2.2, rootY: 0.8, rootSx: 0.98, rootSy: 1.02,
    },
    hit: {
      // bung ngược mu bàn tay ra ngoài, người xoay tới
      shoulderR: dir(FWD, DOWN * 0.12), elbowR: 0.1, handR: 0,
      shoulderL: dir(FWD * 0.3, DOWN * 0.95), elbowL: 1.25,
      torso: 0.42, head: -0.15,
      hipR: -0.4, hipL: 0.45, kneeR: 0.5, kneeL: 0.22,
      rootX: 3, rootY: -1, rootSx: 1.05, rootSy: 0.96,
    },
  },

  // ────────────────────────────────────────────────── CHÉM CẠNH TAY
  chop: {
    wind: {
      // tay trái giơ cao, nhón người lên
      shoulderL: dir(FWD * 0.85, UP * 0.53), elbowL: 1.5, handL: 0.2,
      shoulderR: dir(BACK * 0.45, DOWN * 0.89), elbowR: 1.15,
      torso: -0.18, head: 0.08,
      hipL: 0.2, hipR: -0.2, kneeL: 0.4, kneeR: 0.15,
      rootX: -2, rootY: -2, rootSx: 0.98, rootSy: 1.03,
    },
    hit: {
      // chém chéo xuống, tay duỗi hết tầm (tay trái vẽ SAU thân nên phải duỗi
      // ra ngoài mép thân mới thấy bàn tay), người xoay hông + sụp theo
      shoulderL: dir(FWD * 0.97, DOWN * 0.24), elbowL: 0.15, handL: 0,
      shoulderR: dir(FWD * 0.3, DOWN * 0.95), elbowR: 1.1,
      torso: 0.45, head: -0.2,
      hipL: -0.4, hipR: 0.45, kneeL: 0.5, kneeR: 0.25,
      rootX: 3.2, rootY: 2, rootSx: 1.05, rootSy: 0.95,
    },
  },

  // ──────────────────────────────────────────────────────────────── TÁT
  slap: {
    wind: {
      shoulderL: dir(BACK * 0.9, UP * 0.35), elbowL: 1.5, handL: 0.25,
      shoulderR: dir(FWD * 0.55, DOWN * 0.84), elbowR: 1.1,
      torso: 0.35, head: -0.15,
      hipL: -0.3, hipR: 0.3, kneeL: 0.2, kneeR: 0.45,
      rootX: -2.5, rootY: 1, rootSx: 0.98, rootSy: 1.02,
    },
    hit: {
      // vả ngang: tay đi ~3 rad, người + đầu hất ngược lại
      shoulderL: dir(FWD * 0.9, DOWN * 0.44), elbowL: 0.5, handL: 0,
      shoulderR: dir(BACK * 0.5, DOWN * 0.87), elbowR: 1.2,
      torso: -0.5, head: 0.22,
      hipL: 0.5, hipR: -0.45, kneeL: 0.5, kneeR: 0.25,
      rootX: 3.2, rootY: -1, rootSx: 1.06, rootSy: 0.95,
    },
  },

  // ─────────────────────────────────────────────────────────── VỤT DÉP
  slipper: {
    wind: {
      // nhón người, vung dép qua đầu ra sau
      shoulderR: dir(BACK * 0.85, UP * 0.53), elbowR: 1.6, handR: 0.3,
      shoulderL: dir(BACK * 0.35, DOWN * 0.94), elbowL: 1.3,
      torso: -0.4, head: 0.15,
      hipR: 0.35, hipL: -0.3, kneeR: 0.5, kneeL: 0.2,
      rootX: -3, rootY: -1, rootSx: 0.97, rootSy: 1.05,
    },
    hit: {
      // vụt sụp xuống: tay đi ~3,1 rad, cả người đổ tới rồi sụp
      shoulderR: dir(FWD * 0.85, DOWN * 0.53), elbowR: 0.12, handR: 0,
      shoulderL: dir(FWD * 0.3, DOWN * 0.95), elbowL: 1.2,
      torso: 0.5, head: -0.2,
      hipR: -0.5, hipL: 0.55, kneeR: 0.7, kneeL: 0.3, footR: -0.35,
      rootX: 3.6, rootY: 2, rootSx: 1.05, rootSy: 0.94,
    },
  },

  // ──────────────────────────────────────────────────────────────── CHỎ
  elbow: {
    wind: {
      // gập tay, xoay người ra sau để lấy đà thúc chỏ
      shoulderR: dir(BACK * 0.8, DOWN * 0.6), elbowR: 2.1, handR: 0.2,
      shoulderL: dir(FWD * 0.5, DOWN * 0.87), elbowL: 1.3,
      torso: -0.45, head: 0.15,
      hipR: 0.35, hipL: -0.3, kneeR: 0.5, kneeL: 0.2,
      rootX: -2.8, rootY: 1, rootSx: 0.97, rootSy: 1.03,
    },
    hit: {
      // bước chân vào + xoay hông, chỏ thúc tới (tay vẫn gập)
      shoulderR: dir(FWD * 0.98, DOWN * 0.05), elbowR: 2.3, handR: 0,
      shoulderL: dir(BACK * 0.45, DOWN * 0.89), elbowL: 1.25,
      torso: 0.6, head: -0.25,
      hipR: -0.6, hipL: 0.65, kneeR: 0.8, kneeL: 0.4, footR: -0.3,
      rootX: 4.2, rootY: -0.8, rootSx: 1.07, rootSy: 0.94,
    },
  },

  // ────────────────────────────────────────────────────────── CHƯỞNG
  palm: {
    wind: {
      shoulderR: dir(BACK * 0.6, DOWN * 0.8), elbowR: 1.5, handR: 0.25,
      shoulderL: dir(BACK * 0.4, DOWN * 0.92), elbowL: 1.4,
      torso: -0.3, head: 0.12,
      hipR: 0.3, hipL: -0.3, kneeR: 0.35, kneeL: 0.2,
      rootX: -3, rootY: 1.5, rootSx: 0.97, rootSy: 1.03,
    },
    hit: {
      /*
       * Chưởng phải tới CAO, chưởng trái tới THẤP.
       * Vì sao không đưa hai tay bằng nhau: tay trái vẽ z=25 nằm SAU thân z=30,
       * mà cận chiến không có cờ `frontArms` như dáng ra chiêu ⇒ hai tay cùng tầm
       * thì tay trái biến mất sau thân. Lệch tầng vừa thấy được cả hai tay,
       * vừa đúng bài chưởng (một cao một thấp).
       */
      shoulderR: dir(FWD * 0.95, UP * 0.3), elbowR: 0.08, handR: 0,
      shoulderL: dir(FWD * 0.55, DOWN * 0.84), elbowL: 0.25, handL: 0,
      torso: 0.42, head: -0.2,
      hipR: -0.45, hipL: 0.5, kneeR: 0.6, kneeL: 0.3,
      rootX: 4, rootY: -1.2, rootSx: 1.06, rootSy: 0.95,
    },
  },

  // ──────────────────────────────────────────────────────────────── XÔ
  shove: {
    wind: {
      // co hai chỏ về sau, ngả người
      shoulderR: dir(BACK * 0.5, DOWN * 0.87), elbowR: 1.7,
      shoulderL: dir(BACK * 0.45, DOWN * 0.89), elbowL: 1.7,
      torso: -0.25, head: 0.1,
      hipR: 0.28, hipL: -0.28, kneeR: 0.4, kneeL: 0.25,
      rootX: -3, rootY: 1.2, rootSx: 0.97, rootSy: 1.03,
    },
    hit: {
      // xô tới ngang bụng: hai tay duỗi (trái thấp hơn để không chui sau thân)
      shoulderR: dir(FWD * 0.92, DOWN * 0.4), elbowR: 0.15,
      shoulderL: dir(FWD * 0.3, DOWN * 0.95), elbowL: 0.3,
      torso: 0.4, head: -0.22,
      hipR: -0.5, hipL: 0.55, kneeR: 0.7, kneeL: 0.35,
      rootX: 4.5, rootY: -0.5, rootSx: 1.07, rootSy: 0.94,
    },
  },

  // ─────────────────────────────────────────────────────────────── VẬT
  grapple: {
    wind: {
      // ngồi thấp chụp lấy chân đối thủ
      shoulderR: dir(FWD * 0.75, DOWN * 0.66), elbowR: 0.5,
      shoulderL: dir(FWD * 0.6, DOWN * 0.8), elbowL: 0.6,
      torso: 0.45, head: -0.25,
      hipR: -0.4, hipL: 0.4, kneeR: 0.9, kneeL: 0.9,
      rootX: -1.5, rootY: 6, rootSx: 1.03, rootSy: 0.94,
    },
    hit: {
      // quăng qua vai: hai tay vung ngược lên sau, người ưỡn ngả ra sau
      shoulderR: dir(BACK * 0.85, UP * 0.53), elbowR: 0.3,
      shoulderL: dir(BACK * 0.95, UP * 0.3), elbowL: 0.35,
      torso: -0.5, head: 0.25,
      hipR: 0.5, hipL: -0.5, kneeR: 0.3, kneeL: 0.5,
      rootX: 2.5, rootY: -3, rootSx: 0.97, rootSy: 1.06,
    },
    // vật là đòn nặng -> nảy ít, trọng lượng "đằm"
    spring: 0.35,
  },

  // ────────────────────────────────────────────────── ĐẬP BÁNG SÚNG
  riflebutt: {
    wind: {
      // hai tay nâng báng súng qua đầu, nhón người
      shoulderR: dir(BACK * 0.85, UP * 0.53), elbowR: 1.4,
      shoulderL: dir(FWD * 0.85, UP * 0.53), elbowL: 1.4,
      torso: -0.35, head: 0.2,
      hipR: 0.3, hipL: -0.3, kneeR: 0.4, kneeL: 0.25,
      rootX: -2.6, rootY: -2, rootSx: 0.97, rootSy: 1.05,
    },
    hit: {
      // đập sụp: tay đi ~3,2 rad, khuỵu gối, người sụp xuống
      shoulderR: dir(FWD * 0.9, DOWN * 0.44), elbowR: 0.35,
      shoulderL: dir(FWD * 0.55, DOWN * 0.84), elbowL: 0.45,
      torso: 0.5, head: -0.25,
      hipR: -0.55, hipL: 0.6, kneeR: 0.85, kneeL: 0.5, footR: -0.2,
      rootX: 3.4, rootY: 3, rootSx: 1.06, rootSy: 0.93,
    },
    spring: 0.35,
  },

  // ───────────────────────────────────────────────────────── LÊN GỐI
  knee: {
    wind: {
      // hạ người, hai tay chụp xuống, chân sau lấy đà
      shoulderR: dir(FWD * 0.7, DOWN * 0.71), elbowR: 1.2,
      shoulderL: dir(FWD * 0.5, DOWN * 0.87), elbowL: 1.2,
      torso: 0.3, head: -0.2,
      hipR: 0.25, hipL: -0.1, kneeR: 1.1, kneeL: 0.3,
      rootX: -2, rootY: 5, rootSx: 1.03, rootSy: 0.94,
    },
    hit: {
      // gối thúc lên (hông đi ~2,1 rad) + hai tay kéo xuống, người nhấc lên
      shoulderR: dir(FWD * 0.3, DOWN * 0.95), elbowR: 1.5,
      shoulderL: dir(FWD * 0.2, DOWN * 0.98), elbowL: 1.5,
      torso: -0.25, head: 0.15,
      hipR: dir(FWD * 0.95, UP * 0.3), hipL: 0.15, kneeR: 1.7, kneeL: 0.2, footR: -0.3,
      rootX: 3.5, rootY: -6, rootSx: 0.96, rootSy: 1.09,
    },
  },

  // ───────────────────────────────────────────────────────── ĐÁ NGANG
  kick: {
    wind: {
      shoulderR: dir(BACK * 0.5, DOWN * 0.87), elbowR: 1.1,
      shoulderL: dir(FWD * 0.6, DOWN * 0.8), elbowL: 1.0,
      torso: -0.2, head: 0.08,
      hipR: 0.35, hipL: -0.08, kneeR: 1.3, kneeL: 0.3,
      rootX: -2.2, rootY: 2, rootSx: 1.02, rootSy: 0.96,
    },
    hit: {
      // chân tống ngang tới −1,92 rad (đúng biên độ kickCombo đã chứng minh),
      // tay quạt giữ thăng bằng, người ngả ra sau
      shoulderR: dir(BACK * 0.7, DOWN * 0.5), elbowR: 0.9,
      shoulderL: dir(FWD * 0.9, UP * 0.44), elbowL: 0.7,
      torso: -0.4, head: 0.15,
      hipR: dir(FWD * 0.94, UP * 0.34), hipL: 0.1, kneeR: 0.12, kneeL: 0.25, footR: -0.5,
      rootX: 3.5, rootY: -3, rootSx: 0.97, rootSy: 1.06,
    },
  },

  // ───────────────────────────────────────────────────────── ĐÁ XOAY
  spinkick: {
    wind: {
      /*
       * XOAY NGƯỜI bằng `root.rot` (gốc xương nằm ở MẶT ĐẤT nên xoay như trồng
       * chân trụ). ⚠️ ĐỪNG xoay nửa vòng: rig này là paper-doll 2D nên xoay cả
       * thân >~35° là mắt đọc thành "ngã ngang" chứ không phải "xoay".
       * Vì vậy chỉ xoay ~46° và để CẲNG CHÂN quét hết vòng (hông đi ~2,5 rad).
       */
      rootRot: -0.3,
      shoulderR: dir(BACK * 0.6, DOWN * 0.8), elbowR: 1.2,
      shoulderL: dir(FWD * 0.85, UP * 0.53), elbowL: 0.9,
      torso: -0.3, head: 0.1,
      hipR: 0.9, hipL: -0.15, kneeR: 1.4, kneeL: 0.3,
      rootX: -2.4, rootY: 2, rootSx: 1.02, rootSy: 0.96,
    },
    hit: {
      rootRot: 0.5,
      shoulderR: dir(BACK * 0.9, DOWN * 0.44), elbowR: 0.8,
      shoulderL: dir(FWD * 0.95, UP * 0.3), elbowL: 0.6,
      torso: 0.45, head: -0.2,
      hipR: dir(FWD * 0.99, UP * 0.1), hipL: 0.15, kneeR: 0.1, kneeL: 0.3, footR: -0.45,
      rootX: 2.8, rootY: -4, rootSx: 0.97, rootSy: 1.05,
    },
    // đòn xoay cần "nảy" rõ để thấy người ta xoay rồi trả về
    spring: 0.9,
  },

  // ───────────────────────────────────────────────────────── MÓC CHÂN
  tripkick: {
    wind: {
      // hạ người, chân trái co về sau, người chúi tới
      shoulderR: dir(FWD * 0.6, DOWN * 0.8), elbowR: 1.1,
      shoulderL: dir(BACK * 0.5, DOWN * 0.87), elbowL: 1.1,
      torso: 0.4, head: -0.2,
      hipL: 0.7, hipR: -0.1, kneeL: 1.3, kneeR: 0.4,
      rootRot: -0.25,
      rootX: -2.2, rootY: 4, rootSx: 1.03, rootSy: 0.95,
    },
    hit: {
      // chân trái móc ngang tầm thấp (hông đi gần 2 rad) + xoay người theo vòng móc
      shoulderR: dir(BACK * 0.6, DOWN * 0.8), elbowR: 1.0,
      shoulderL: dir(FWD * 0.5, DOWN * 0.87), elbowL: 1.0,
      torso: -0.4, head: 0.15,
      hipL: dir(FWD * 0.95, DOWN * 0.3), hipR: 0.15, kneeL: 0.2, kneeR: 0.35, footL: -0.4,
      rootRot: 0.4,
      rootX: 3.6, rootY: -1, rootSx: 1.06, rootSy: 0.95,
    },
    spring: 0.8,
  },

  // ─────────────────────────────────────────────────────── QUÉT CƯỚC
  sweep: {
    wind: {
      // ngồi sụp xuống (squash dọc), hai gối gập
      shoulderR: dir(FWD * 0.5, DOWN * 0.87), elbowR: 1.2,
      shoulderL: dir(BACK * 0.3, DOWN * 0.95), elbowL: 1.2,
      torso: 0.45, head: -0.3,
      hipR: 0.5, hipL: -0.3, kneeR: 1.6, kneeL: 1.2,
      rootX: -2, rootY: 8, rootSx: 1.06, rootSy: 0.9,
    },
    hit: {
      // hạ thấp hẳn rồi quét chân vòng dưới đất, tay chống giữ thăng bằng
      shoulderR: dir(BACK * 0.7, DOWN * 0.71), elbowR: 0.9,
      shoulderL: dir(FWD * 0.9, DOWN * 0.44), elbowL: 1.0,
      torso: 0.5, head: -0.35,
      hipR: dir(FWD * 0.85, DOWN * 0.53), hipL: -0.6, kneeR: 0.35, kneeL: 1.8, footR: -0.3,
      rootX: 3.5, rootY: 12, rootSx: 1.1, rootSy: 0.88,
    },
    spring: 0.4,
  },

  // ───────────────────────────────────────────────────────── HÚC ĐẦU
  headbutt: {
    wind: {
      // ngửa hẳn ra sau, hai tay kéo về sau
      shoulderR: dir(BACK * 0.6, DOWN * 0.8), elbowR: 1.3,
      shoulderL: dir(BACK * 0.5, DOWN * 0.87), elbowL: 1.3,
      torso: -0.5, head: 0.5,
      hipR: 0.3, hipL: -0.3, kneeR: 0.6, kneeL: 0.3,
      rootX: -3, rootY: 1.5, rootSx: 0.97, rootSy: 1.03,
    },
    hit: {
      // húc tới: thân đi 1,2 rad + đầu 1,05 rad, hai tay bung ra sau, hông đẩy
      shoulderR: dir(BACK * 0.85, DOWN * 0.53), elbowR: 0.8,
      shoulderL: dir(BACK * 0.75, DOWN * 0.66), elbowL: 0.8,
      torso: 0.7, head: -0.55,
      hipR: -0.6, hipL: 0.7, kneeR: 0.9, kneeL: 0.4,
      rootX: 4, rootY: -0.5, rootSx: 1.07, rootSy: 0.94,
    },
    // húc đầu là cú "cứng" -> ít nảy
    spring: 0.3,
  },
};

/** Thế võ này đã có dáng riêng chưa? (test dùng để bắt lỗi "quên thêm case") */
export function hasMeleePose(id: string): boolean {
  return Object.prototype.hasOwnProperty.call(MELEE_POSES, id);
}

/** Id các thế võ có dáng — để test đối chiếu với bảng MOVES. */
export const MELEE_POSE_IDS = Object.keys(MELEE_POSES);

export function applyPose(rig: Rig, p: PoseInput): void {
  const b = (n: string): Bone => rig.tryBone(n) ?? noop;
  rig.resetPose();
  const root = b('root');
  const torso = b('torso');
  const head = b('head');

  /*
   * QUAN TRỌNG: KHÔNG nhân `facing` vào scaleX của xương.
   * Ma trận có định thức âm sẽ khiến matRotation() trả về 180° ⇒ cả nhân vật
   * LỘN NGƯỢC thay vì lật ngang. Việc lật được làm ở lớp sprite (setFlipX +
   * đảo toạ độ x), xem render/unitview.ts.
   */
  root.sx = 1;
  root.sy = 1;
  root.rot = 0;
  root.x = 0;
  root.y = 0;

  // RA CHIÊU được ưu tiên hơn đánh thường và đi bộ — để đòn đọc rõ ràng
  if (p.cast && p.castT !== undefined && p.castT < 1) {
    applyCastAnim(rig, p.cast, p.castT);
    return;
  }

  const phase = p.seed * TAU;

  if (p.state === 'dead') {
    const k = Math.min(1, (p.deadFor ?? 0) / 0.45);
    const e = 1 - Math.pow(1 - k, 3);
    root.rot = -e * Math.PI * 0.48;
    root.y = -e * 2;
    torso.rot = -e * 0.2;
    head.rot = e * 0.3;
    return;
  }

  if (p.state === 'move') {
    const f = 7.5;
    const sw = Math.sin(p.t * f + phase);
    b('hipL').rot = sw * 0.45;
    b('hipR').rot = -sw * 0.45;
    b('kneeL').rot = Math.max(0, -sw) * 0.75 + 0.08;
    b('kneeR').rot = Math.max(0, sw) * 0.75 + 0.08;
    b('footL').rot = -b('kneeL').rot * 0.35;
    b('footR').rot = -b('kneeR').rot * 0.35;
    b('shoulderL').rot = -sw * 0.55 + 0.16;
    b('shoulderR').rot = sw * 0.55 - 0.16;
    b('elbowL').rot = 0.35 + Math.max(0, sw) * 0.4;
    b('elbowR').rot = 0.35 + Math.max(0, -sw) * 0.4;
    root.y = -Math.abs(sw) * 1.6;
    torso.rot = 0.06 + sw * 0.05;
    head.rot = -0.04;
    return;
  }

  // ---- idle / attack
  const breath = Math.sin(p.t * 2.1 + phase);
  torso.rot = 0.02 + breath * 0.025;
  head.rot = -breath * 0.03;
  root.y = breath * 0.8;

  b('shoulderL').rot = 0.2 + breath * 0.05;
  b('shoulderR').rot = -0.2 - breath * 0.05;
  b('elbowL').rot = 0.45;
  b('elbowR').rot = 0.45;
  b('hipL').rot = 0.04;
  b('hipR').rot = -0.04;
  b('kneeL').rot = 0.05;
  b('kneeR').rot = 0.05;

  if (p.isCasting) {
    const k = Math.min(1, p.swing);
    torso.rot = -0.12 * k;
    b('shoulderL').rot = -2.1;
    b('shoulderR').rot = 2.1;
    b('elbowL').rot = 0.5;
    b('elbowR').rot = 0.5;
    head.rot = -0.12 * k;
    return;
  }

  // ---------------------------------------------------------------- RA ĐÒN VÕ
  if (p.swing > 0) {
    const prog = 1 - p.swing; // 0..1
    const { windEnd, hitEnd } = MELEE_PHASE;

    // ba nhịp: lấy đà → ra đòn → thu về
    const wind = prog < windEnd ? prog / windEnd : 1;
    const strike = prog < windEnd ? 0 : prog < hitEnd ? (prog - windEnd) / (hitEnd - windEnd) : 1;
    const back = prog < hitEnd ? 0 : (prog - hitEnd) / (1 - hitEnd);

    const strikeS = easeOutCubic(strike); // bung nhanh rồi chậm dần khi tới đích
    const windW = easeInOutCubic(wind) * (1 - strikeS); // lấy đà rồi nhả
    const hitS = strikeS * (1 - easeInCubic(back)); // thu về: trả dần về dáng đứng
    /*
     * ĐÀN HỒI: một dao động tắt dần chỉ có mặt ở nhịp THU VỀ (bằng 0 ở cả hai
     * đầu nhịp nên không giật) — vượt qua tư thế đứng một chút rồi lòi lại.
     */
    const spring = Math.sin(back * Math.PI * 2) * (1 - back);

    const pose = MELEE_POSES[p.move ?? 'punch'] ?? MELEE_POSES.punch;
    const springK = pose.spring ?? 0.6;

    /** Góc/dịch của một khoá: trộn từ BASE → LẤY ĐÀ → RA ĐÒN → (thu về) BASE. */
    const out = (k: Key): number => {
      const b0 = BASE[k];
      const w = pose.wind[k] ?? b0;
      const h = pose.hit[k] ?? b0;
      let v = b0 + (w - b0) * windW + (h - b0) * hitS;
      if (spring !== 0) v += spring * springK * 0.18 * (b0 - h);
      return v;
    };

    for (const k of ROT_KEYS) b(k).rot = out(k);
    root.rot = out('rootRot');
    root.x = out('rootX');
    root.y = out('rootY');
    root.sx = out('rootSx');
    root.sy = out('rootSy');
    return;
  }

  if (p.hitFlash > 0.02) {
    const f = p.hitFlash;
    torso.rot = -0.24 * f;
    head.rot = -0.2 * f;
    b('shoulderL').rot = -0.7 * f;
    b('shoulderR').rot = 0.7 * f;
    b('hipL').rot = -0.16 * f;
    b('hipR').rot = 0.16 * f;
    root.x = -1.6 * f;
    root.y = -1.2 * f;
  }
}
